import type { FC } from 'react';
import { useMemo } from 'react';
import { Flex } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import type * as CSS from 'csstype';
import { useBasket } from '../../bff-api/baskets/useBasket';
import { useEstimatedDeliveryDate } from '../../bff-api/delivery/useEstimatedDeliveryDate';
import { getDefaultShipment } from '../../bff-api/utils/basket-utils';
import { useUserZipCode } from '../../hooks/useUserZipCode';
import { Button, Text, Tooltip, TooltipPlacement } from '../../ui-components';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';

export type PfaWarningProps = {
    id: string;
    containsPfa?: boolean;
    stateCode?: string;
    alignItems: CSS.Property.AlignItems;
};

const PfaWarning: FC<PfaWarningProps> = ({ id, containsPfa = false, alignItems, stateCode }) => {
    const intl = useIntl();
    const zipCode = useUserZipCode().data ?? '';
    const edd = useEstimatedDeliveryDate(zipCode);
    const { data: basket } = useBasket();
    const pfaRestrictedStates = useCustomPreferenceOrDefault<string[]>(
        CustomPreference.PFA_RESTRICTED_STATES,
        [],
    );
    const stateCodeClean = useMemo(() => {
        if (stateCode) {
            return stateCode;
        }
        if (basket?.c_paypalErrorMessage) {
            const address = getDefaultShipment(basket)?.shippingAddress;
            if (address?.stateCode) {
                return address?.stateCode;
            }
        }
        return edd.data?.stateCode;
    }, [stateCode, edd.data?.stateCode, basket]);
    const showPfaMessage = useMemo(
        () =>
            Boolean(containsPfa && stateCodeClean && pfaRestrictedStates.includes(stateCodeClean)),
        [containsPfa, pfaRestrictedStates, stateCodeClean],
    );

    return showPfaMessage ? (
        <Flex data-testid={`${id}-container`} direction="column" alignItems={alignItems} pb={1}>
            <Text id={`${id}-text-container`} color="slt-red-error" style="regular" size="small">
                <FormattedMessage
                    id="pfa.pdp.message.content"
                    defaultMessage="We're sorry, this item is restricted to ship to your location"
                />{' '}
                <Tooltip
                    label={intl.formatMessage({
                        defaultMessage:
                            'This item is made with PFAS chemicals and cannot be shipped to some states.',
                        id: 'pfa.pdp.message.button.tooltip',
                    })}
                    placement={TooltipPlacement.topLeft}
                >
                    <Button id={`${id}-details-button`} variant="text-button" color="slt-red-error">
                        <FormattedMessage id="pfa.pdp.message.button" defaultMessage="Details" />
                    </Button>
                </Tooltip>
            </Text>
        </Flex>
    ) : null;
};

PfaWarning.defaultProps = {
    containsPfa: false,
};

export { PfaWarning };
